import moment from "moment";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { Modal, Button, Form } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
  ACTIVITY_TYPES,
  ACTIVITY_RESULT_TYPES,
  APPOINTMENT_TYPE
} from "../strings";
import { AppointmentDateDialogSchema } from "../../services/validation-schema";
import { useTranslation } from "react-i18next";
import { showError } from "../../services/toast-service";
import RadioInput from "../reusable-form-fields/input-radio";
import BusinessTimeInfo from "./business-time-info";
import {
  formatLocal,
  localToInstant,
  pickerDateToLocal
} from "../../services/business-time";

const AppointmentPickerDialog = ({
  show,
  onHide,
  activityHandler,
  allowClose = true,
  comment,
  responses,
  forLS = false,
  funnelAppointmentType
}) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const errorBorderClasses = "border border-2 border-danger";
  const initialValues = { date: "", appointmentType: "" };
  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(
      AppointmentDateDialogSchema(forLS, t("appointmentTypes.required"))
    )
  });
  const {
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    register,
    getValues,
    formState: { errors }
  } = methods;
  const selectedDate = watch("date");

  const qualifyWithoutAppointment = async () => {
    try {
      if (isUpdating) return;
      setIsUpdating(true);
      await activityHandler({
        type: ACTIVITY_TYPES.CALL,
        result: ACTIVITY_RESULT_TYPES.QUALIFIED,
        comment: `${comment} - (Qualified Without Appointment)`,
        responses
      });
      handleCloseDialog();
    } catch (error) {
      console.error("Error in forwarding without appointment", error);
      showError(error.response?.data?.message || t("common.errorOccurred"));
    } finally {
      setIsUpdating(false);
    }
  };

  const submitAppointment = async (values) => {
    try {
      if (isUpdating) return;
      setIsUpdating(true);
      const resultType = forLS
        ? ACTIVITY_RESULT_TYPES.QUALIFIED
        : ACTIVITY_RESULT_TYPES.APPOINTMENT_POSTPONED;
      // What the agent typed is German time, whatever timezone the PC runs
      // on: it is sent as wall clock and never converted with the PC's zone.
      const localDateTime = pickerDateToLocal(values.date);
      let appointmentOn;
      try {
        appointmentOn = localToInstant(localDateTime).toISOString();
      } catch (error) {
        setError("date", {
          type: "businessTime",
          message: t("leadAgent.appointmentTimeInvalid")
        });
        return;
      }
      const typedTime = formatLocal(localDateTime);
      const note = forLS
        ? `${t("appointmentNote.book").trim()} ${typedTime}. ${comment}`
        : `${t("appointmentNote.postpone").trim()} ${typedTime}`;

      await activityHandler({
        type: ACTIVITY_TYPES.CALL,
        result: resultType,
        comment: note,
        responses,
        appointmentDetails: {
          localDateTime,
          date: appointmentOn,
          ...(forLS && { type: values.appointmentType })
        }
      });
      handleCloseDialog();
    } catch (error) {
      console.error("Error in creating appointment activity", error);
      showError(error.response?.data?.message || t("common.errorOccurred"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseDialog = () => {
    reset({ ...getValues(), date: "" });
    onHide();
  };

  useEffect(() => {
    if (funnelAppointmentType) {
      setValue("appointmentType", funnelAppointmentType);
    }
  }, [funnelAppointmentType, setValue]);

  return (
    <Modal
      show={show}
      centered
      size="md"
      backdrop="static"
      // className="text-center"
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <Modal.Header className="justify-content-center">
        <h5 className="mb-0">{t("leadAgent.appointmentDialogHeading")}</h5>
      </Modal.Header>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(submitAppointment)}>
          <div className="d-flex flex-row justify-content-center p-3">
            <div className="text-center my-4">
              <ReactDatePicker
                selected={selectedDate}
                minDate={moment().toDate()}
                dateFormat="MMMM d, yyyy h:mm aa"
                onChange={(selectedDate) => {
                  setValue("date", selectedDate);
                  clearErrors("date");
                }}
                calendarClassName={errors?.date ? errorBorderClasses : ""}
                showTimeInput // Enables time selection
                timeInputLabel="Time:"
                inline
              />
              {errors?.date && (
                <div className="text-danger fw-bold">
                  {errors?.date?.message}*
                </div>
              )}
              <BusinessTimeInfo
                localDateTime={
                  selectedDate ? pickerDateToLocal(selectedDate) : ""
                }
              />
            </div>
            {forLS && (
              <div className="p-2 my-4">
                <h6 className="text-center mb-3">
                  {t("leadAgent.appointmentType")}
                </h6>
                <RadioInput
                  isInline={false}
                  firstOptLabel={t("appointmentTypes.onSiteAtLeadAddress")}
                  secondOptLabel={t("appointmentTypes.onSiteAtCompanyAddress")}
                  thirdOptLabel={t("appointmentTypes.phoneAppointment")}
                  firstValue={APPOINTMENT_TYPE.ONSITE_AT_LEAD_ADDRESS}
                  firstValueId={APPOINTMENT_TYPE.ONSITE_AT_LEAD_ADDRESS}
                  secondValue={APPOINTMENT_TYPE.ONSITE_AT_COMPANY_ADDRESS}
                  secondValueId={APPOINTMENT_TYPE.ONSITE_AT_COMPANY_ADDRESS}
                  thirdValue={APPOINTMENT_TYPE.PHONE_APPOINTMENT}
                  thirdValueId={APPOINTMENT_TYPE.PHONE_APPOINTMENT}
                  name="appointmentType"
                  errors={errors?.appointmentType}
                  register={register}
                />
              </div>
            )}
          </div>
          <Modal.Footer className="justify-content-between">
            {allowClose ? (
              <Button
                disabled={isUpdating}
                onClick={handleCloseDialog}
                variant="outline-secondary"
              >
                {t("common.close")}
              </Button>
            ) : (
              <Button
                disabled={isUpdating}
                onClick={qualifyWithoutAppointment}
                variant="danger"
              >
                {t("leadAgent.forwardWithoutAppointmnet")}
              </Button>
            )}
            <Button disabled={isUpdating} type="submit" variant="success">
              {t("leadAgent.saveAppointment")}
            </Button>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default AppointmentPickerDialog;
